import {defaultFilterProviderOptions} from '../constants/defaults'
import {FILTER_KEYS, FILTER_VALUES} from '../constants/filter-constants'
import {Strings} from '../constants/strings'
import type {FilterQuery} from '../filter-query'
import {
  type FilterBlock,
  type FilterKey,
  FilterOperator,
  FilterProviderType,
  type FilterSuggestion,
  type IndexedBlockValueItem,
  type SuppliedFilterProviderOptions,
  ValidationTypes,
} from '../types'
import {getUnescapedFilterValue} from '../utils'
import {
  getEmptyValueValidation,
  getInvalidValueValidation,
  getMultiValueValidation,
  StaticFilterProvider,
} from './static'

const POSITIVE_NUMBER_REGEX = /^(?!-)(?:>|<|>=|<=)?(\d+)$/
const NEGATIVE_NUMBER_REGEX = /^(?:>|<|>=|<=)?-(\d+)$/
const POSITIVE_DECIMAL_NUMBER_REGEX = /^(?:>|<|>=|<=)?(\d+\.\d+|\d+)$/
const NEGATIVE_DECIMAL_NUMBER_REGEX = /^(?:>|<|>=|<=)?-(\d+\.\d+|\d+)$/
const operators = ['>=', '<=', '>', '<']

type NumericFilterOptions = {
  positive?: boolean
  negative?: boolean
  decimal?: boolean
} & SuppliedFilterProviderOptions

export class NumericFilterProvider extends StaticFilterProvider {
  positive: boolean
  negative: boolean
  decimal: boolean

  constructor(filterKey: FilterKey, filterValues: FilterSuggestion[], options?: NumericFilterOptions) {
    super(filterKey, filterValues, {
      ...defaultFilterProviderOptions,
      ...options,
      filterTypes: {
        ...defaultFilterProviderOptions.filterTypes,
        valueless: false,
        exclusive: false,
        multiKey: false,
        multiValue: false,
        ...options?.filterTypes,
      },
    })
    this.positive = options?.positive ?? true
    this.negative = options?.negative ?? false
    this.decimal = options?.decimal ?? false

    this.filterValues = filterValues.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
    this.type = FilterProviderType.Number
  }

  override validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
  ): IndexedBlockValueItem[] {
    return values.map((v, i) => {
      if (!this.allowsFilterValues()) {
        return {
          ...v,
          valid: false,
          validations: [{type: ValidationTypes.InvalidValue, message: Strings.filterValuesUnsupported(this.key)}],
        }
      }

      if (
        !this.options.filterTypes.multiValue &&
        ((block.operator === FilterOperator.Between && i > 1) || (block.operator !== FilterOperator.Between && i > 0))
      ) {
        return getMultiValueValidation(v, this.key)
      }

      const extractedValue = getUnescapedFilterValue(v.value)

      if (!extractedValue) return getEmptyValueValidation(v, this.key)

      if (block.operator === FilterOperator.Between && operators.some(op => extractedValue.startsWith(op))) {
        return getInvalidValueValidation(v, extractedValue, this.key)
      }

      // Check if the value matches the number format (positive or negative)
      let isPositiveNumber = false
      let isNegativeNumber = false
      let isDecimalNumber = false

      // Check for positive numbers
      if (this.decimal) {
        const positiveDecimalMatch = extractedValue.match(POSITIVE_DECIMAL_NUMBER_REGEX)
        isPositiveNumber = (positiveDecimalMatch?.length ?? 0) > 0 && positiveDecimalMatch?.[0] === extractedValue
        isDecimalNumber = isPositiveNumber && extractedValue.includes('.')
      } else {
        const positiveMatch = extractedValue.match(POSITIVE_NUMBER_REGEX)
        isPositiveNumber = (positiveMatch?.length ?? 0) > 0 && positiveMatch?.[0] === extractedValue
      }

      // Check for negative numbers
      if (this.decimal) {
        const negativeDecimalMatch = extractedValue.match(NEGATIVE_DECIMAL_NUMBER_REGEX)
        isNegativeNumber = (negativeDecimalMatch?.length ?? 0) > 0 && negativeDecimalMatch?.[0] === extractedValue
        if (isNegativeNumber && extractedValue.includes('.')) {
          isDecimalNumber = true
        }
      } else {
        const negativeMatch = extractedValue.match(NEGATIVE_NUMBER_REGEX)
        isNegativeNumber = (negativeMatch?.length ?? 0) > 0 && negativeMatch?.[0] === extractedValue
      }

      // Determine if the value is valid based on configuration
      const isValidNumberFormat = isPositiveNumber || isNegativeNumber
      const isValidSign = (this.positive && isPositiveNumber) || (this.negative && isNegativeNumber)
      const isValidDecimal = this.decimal || !isDecimalNumber

      const typeValidity = isValidNumberFormat && isValidSign && isValidDecimal
      return {...v, valid: typeValidity}
    })
  }
}

export class CommentsFilterProvider extends NumericFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {multiValue: false, valueless: false}}
    super(FILTER_KEYS.comments, FILTER_VALUES.comments, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
    })
  }
}

export class InteractionsFilterProvider extends NumericFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {multiValue: false, valueless: false}}
    super(FILTER_KEYS.interactions, FILTER_VALUES.interactions, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
    })
  }
}

export class ReactionsFilterProvider extends NumericFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.reactions, FILTER_VALUES.reactions, {
      ...options,
      filterTypes: {multiValue: false, valueless: false, ...options?.filterTypes},
    })
  }
}

export class SuggestionsFilterProvider extends NumericFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.suggestions, FILTER_VALUES.suggestions, {
      ...options,
      filterTypes: {multiValue: false, valueless: true, hasValue: true, ...options?.filterTypes},
    })
  }
}
